<script lang="ts">
	import X from '@lucide/svelte/icons/x'
	import { demoPath, get, send, setToken, signupLink, type Provider } from '../lib/api'
	import { getDash } from '../lib/dash.svelte'
	import { otherAccount, PROVIDER_NAME } from '../lib/account'
	import BrandIcon from './BrandIcon.svelte'
	import Logo from './Logo.svelte'

	type Mode = 'signin' | 'signup' | 'forgot' | 'reset' | 'invite'
	interface Invite {
		email: string
		role: string
		team: string
		site: string | null
		invited_by: string | null
		has_account: boolean
	}

	const dash = getDash()
	const emailLink = dash.authLink
	let mode = $state<Mode>(emailLink?.kind ?? (signupLink ? 'signup' : 'signin'))
	// a Start free link for someone already signed in, or on a server with sign-up closed
	$effect(() => {
		if (mode !== 'signup' || !dash.me) return
		if (dash.signedIn) dash.authOpen = false
		else if (!dash.me.signups) mode = 'signin'
	})
	let email = $state('')
	let name = $state('')
	let password = $state('')
	let error = $state<string | null>(dash.loginError)
	let note = $state<string | null>(null)
	let busy = $state(false)
	let invite = $state<Invite | null>(null)

	if (emailLink?.kind === 'invite') {
		get<Invite>(`/api/v1/invitations/${emailLink.token}`, undefined, 0)
			.then((i) => {
				invite = i
				email = i.email
			})
			.catch((e) => (error = (e as Error).message))
	}

	const ROLE: Record<string, string> = {
		owner: 'an owner',
		admin: 'an admin',
		editor: 'an editor',
		viewer: 'a viewer',
		billing: 'billing',
	}

	/** Signs in with the token and opens the dashboard at a clean URL. */
	/** Leaves an invitation or reset link for the dashboard (or the sign-in screen it was on). */
	function leave() {
		if (emailLink) history.replaceState(null, '', '/')
		dash.loginError = null
		dash.authOpen = false
		dash.authLink = null
	}

	/** Signs out of the account an invitation isn't for, so you can sign in or sign up as the invited email. */
	async function switchAccount() {
		await send('POST', '/api/v1/auth/logout').catch(() => {})
		setToken(null)
		error = null
		await dash.load()
	}

	const wrong = $derived(invite ? otherAccount(invite.email, dash.me) : null)

	/** Off to Google, which sends the browser back with a one-time code. With an invitation, the sign-in accepts it. */
	async function social(p: Provider) {
		error = dash.loginError = null
		busy = true
		try {
			const r = await send<{ url: string }>('POST', `/api/v1/auth/oauth/${p}`, invite && emailLink ? { invite: emailLink.token } : {})
			location.href = r.url
		} catch (e) {
			error = (e as Error).message
			busy = false
		}
	}
	const providers = $derived(dash.me?.providers ?? [])
	// sign-in, sign-up, and an invitation for someone not yet signed in
	const showSocial = $derived(providers.length > 0 && !dash.signedIn && (mode === 'signin' || mode === 'signup' || (mode === 'invite' && !!invite)))

	async function enter(token: string | undefined, site?: string | null) {
		if (token) setToken(token)
		const to = site ? `/?site=${encodeURIComponent(site)}` : '/'
		// the demo never reads the saved session, so leave it for the dashboard
		if (demoPath) return location.assign(to)
		history.replaceState(null, '', to)
		// a failed Google sign-in before this one must not greet the next sign-in screen
		dash.loginError = null
		dash.authOpen = false
		dash.authLink = null
		if (site) dash.site = site
		await dash.load()
		dash.tick++
	}

	async function submit() {
		error = null
		note = null
		busy = true
		try {
			if (mode === 'signin') {
				const r = await send<{ token: string }>('POST', '/api/v1/auth/login', { email, password })
				if (invite && emailLink) {
					setToken(r.token)
					const a = await send<{ site: string | null }>('POST', `/api/v1/invitations/${emailLink.token}/accept`, {})
					await enter(undefined, a.site)
				} else await enter(r.token)
			} else if (mode === 'signup') {
				await enter((await send<{ token: string }>('POST', '/api/v1/auth/signup', { email, name, password })).token)
			} else if (mode === 'forgot') {
				const r = await send<{ sending: boolean }>('POST', '/api/v1/auth/forgot', { email })
				note = r.sending ? `If ${email} has an account, a reset link is on its way.` : 'Email is not set up on this server; ask whoever runs it to reset your password.'
			} else if (mode === 'reset' && emailLink) {
				await enter((await send<{ token: string }>('POST', '/api/v1/auth/reset', { token: emailLink.token, password })).token)
			} else if (mode === 'invite' && emailLink) {
				const a = await send<{ token?: string; site: string | null }>('POST', `/api/v1/invitations/${emailLink.token}/accept`, dash.signedIn ? {} : { name, password })
				await enter(a.token, a.site)
			}
		} catch (e) {
			error = (e as Error).message
		} finally {
			busy = false
		}
	}

	const title = $derived(
		mode === 'invite'
			? invite
				? `Join ${invite.site ?? invite.team}`
				: 'Invitation'
			: mode === 'signup'
				? 'Start counting'
				: mode === 'forgot'
					? 'Forgot it?'
					: mode === 'reset'
						? 'New password'
						: 'Welcome back',
	)
	const ready = $derived(
		mode === 'forgot' ? !!email : mode === 'reset' ? password.length >= 10 : mode === 'invite' ? dash.signedIn || password.length >= 10 : !!email && password.length >= (mode === 'signup' ? 10 : 1),
	)
</script>

<!-- Back from Google's page can restore this screen from the back/forward cache, still busy -->
<svelte:window onpageshow={(e) => e.persisted && (busy = false)} />

<main class="auth">
	<!-- signed out with no site behind it, the sign-in screen is the page: nothing to close to -->
	{#if dash.signedIn || dash.site || dash.isSample}<button class="btn ghost icon close" onclick={leave} aria-label="Close"><X size={16} /></button>{/if}
	<section class="card sheet">
		<Logo size={30} />
		<span class="kicker">{mode === 'invite' ? 'invitation' : mode === 'signup' ? 'new account' : 'sign in'}</span>
		<h1>{title}</h1>

		{#if mode === 'invite'}
			{#if invite}
				<p class="muted">
					{invite.invited_by ?? 'Someone'} invited <strong>{invite.email}</strong> to {invite.site ? `see ${invite.site}` : `the ${invite.team} team`} as {ROLE[invite.role] ?? invite.role}.
					{#if invite.has_account && !dash.signedIn}Sign in to accept.{:else if !dash.signedIn}{providers.length ? `Continue with ${providers.map((p) => PROVIDER_NAME[p]).join(' or ')}, or pick a password, to create your account.` : 'Pick a password to create your account.'}{/if}
				</p>
				{#if invite.has_account && !dash.signedIn}
					<button class="btn primary wide" onclick={() => (mode = 'signin')}>Sign in as {invite.email}</button>
				{:else if wrong}
					<p class="muted">You're signed in as <strong>{wrong}</strong>. Sign out to accept it as {invite.email}.</p>
					<button class="btn primary wide" onclick={switchAccount}>Sign out</button>
				{/if}
			{/if}
		{:else if mode === 'signin'}
			<p class="muted">{invite ? `Sign in as ${invite.email} to accept the invitation.` : 'Sign in to see your sites and your team.'}</p>
		{:else if mode === 'signup'}
			<p class="muted">An account, a team for your sites, and a script tag. No cookies on your visitors.</p>
		{:else if mode === 'forgot'}
			<p class="muted">We'll email you a link to choose a new password.</p>
		{:else}
			<p class="muted">At least 10 characters. You'll be signed out everywhere else.</p>
		{/if}

		{#if showSocial}
			<div class="social">
				{#each providers as p (p)}
					<button class="btn wide" onclick={() => social(p)} disabled={busy}><BrandIcon brand={p} size={16} /> Continue with {PROVIDER_NAME[p]}</button>
				{/each}
			</div>
			{#if mode !== 'invite' || !(invite?.has_account && !dash.signedIn)}<p class="or"><span>or with email</span></p>{/if}
		{/if}

		{#if mode !== 'invite' || (invite && !(invite.has_account && !dash.signedIn) && !wrong)}
			<form
				onsubmit={(e) => {
					e.preventDefault()
					submit()
				}}
			>
				{#if mode === 'signup' || (mode === 'invite' && !dash.signedIn)}
					<label><span>Name</span><input class="input" autocomplete="name" bind:value={name} placeholder="Ada Lovelace" /></label>
				{/if}
				{#if mode === 'signin' || mode === 'signup' || mode === 'forgot'}
					<label><span>Email</span><input class="input" type="email" autocomplete="email" bind:value={email} readonly={!!invite} placeholder="you@example.com" /></label>
				{/if}
				{#if mode !== 'forgot' && !(mode === 'invite' && dash.signedIn)}
					<label>
						<span>Password</span>
						<input class="input" type="password" autocomplete={mode === 'signin' ? 'current-password' : 'new-password'} bind:value={password} placeholder={mode === 'signin' ? '' : '10 characters or more'} />
					</label>
				{/if}
				<button class="btn primary wide" disabled={!ready || busy}>
					{busy ? '…' : mode === 'signin' ? 'Sign in' : mode === 'signup' ? 'Create account' : mode === 'forgot' ? 'Send the link' : mode === 'reset' ? 'Save and sign in' : 'Accept invitation'}
				</button>
			</form>
		{/if}

		{#if error}<p class="error">{error}</p>{/if}
		{#if note}<p class="note">{note}</p>{/if}

		<footer>
			{#if mode === 'signin'}
				<button class="link" onclick={() => (mode = 'forgot')}>Forgot your password?</button>
				{#if dash.me?.signups && !invite}<button class="link" onclick={() => (mode = 'signup')}>Create an account</button>{/if}
			{:else if mode === 'signup' || mode === 'forgot'}
				<button class="link" onclick={() => (mode = 'signin')}>I have an account</button>
			{:else if mode === 'reset'}
				<button class="link" onclick={() => (mode = 'forgot')}>Send me a new link</button>
			{/if}
		</footer>
	</section>
	{#if dash.me?.sample && !dash.signedIn && !dash.isSample}<a class="demo" href="/demo">Open the live demo <span aria-hidden="true">→</span></a>{/if}
</main>

<style>
	.auth {
		position: fixed;
		inset: 0;
		z-index: 50;
		display: grid;
		place-items: center;
		/* safe: a sheet taller than the screen scrolls from its top instead of losing it;
		   browsers without `safe` keep the plain center before it */
		align-content: center;
		align-content: safe center;
		gap: 16px;
		padding: 24px 16px;
		overflow-y: auto;
		background: var(--bg);
		background-image: radial-gradient(var(--line) 1px, transparent 1px);
		background-size: 14px 14px;
	}
	.demo {
		font-size: 13px;
		font-weight: 600;
		color: var(--text-2);
		text-decoration: none;
	}
	.demo:hover {
		color: var(--text);
	}
	.close {
		position: absolute;
		top: 18px;
		right: 18px;
	}
	.sheet {
		width: min(420px, 100%);
		padding: 28px 28px 22px;
		display: flex;
		flex-direction: column;
		gap: 10px;
	}
	.kicker {
		margin-top: 14px;
		font-family: var(--font-mono);
		font-size: 11px;
		font-weight: 600;
		letter-spacing: 0.12em;
		text-transform: uppercase;
		color: var(--miso);
	}
	h1 {
		margin: 0;
		font-size: 34px;
		line-height: 1.05;
		text-transform: uppercase;
		letter-spacing: -0.01em;
	}
	p {
		margin: 0;
		font-size: 13.5px;
		line-height: 1.55;
	}
	form {
		display: flex;
		flex-direction: column;
		gap: 12px;
		margin-top: 8px;
	}
	label {
		display: flex;
		flex-direction: column;
		gap: 5px;
	}
	label span {
		font-size: 11px;
		font-weight: 600;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--text-3);
	}
	.wide {
		width: 100%;
		height: 40px;
		justify-content: center;
		margin-top: 4px;
	}
	.error {
		color: var(--miso-strong);
	}
	.social {
		display: flex;
		flex-direction: column;
		gap: 8px;
		margin-top: 8px;
	}
	.social .wide {
		gap: 10px;
		margin-top: 0;
	}
	.or {
		display: flex;
		align-items: center;
		gap: 10px;
		font-size: 11px;
		font-weight: 600;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--text-3);
	}
	.or::before,
	.or::after {
		content: '';
		flex: 1;
		border-top: 1.5px solid var(--line);
	}
	.note {
		padding: 10px 12px;
		border: 1.5px dashed var(--miso-line);
		border-radius: var(--radius-sm);
	}
	footer {
		display: flex;
		justify-content: space-between;
		gap: 12px;
		margin-top: 8px;
	}
	.link {
		font-size: 12.5px;
		color: var(--text-3);
		text-decoration: underline;
		text-underline-offset: 3px;
	}
	.link:hover {
		color: var(--text);
	}
</style>
